import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { formatCountdown } from '../../calendar/dates';
import {
  formatPhone,
  useConnections,
  useRefreshWhatsapp,
  useStartWhatsapp,
  useWhatsappStatus,
  type WhatsappState,
  type WhatsappStatus,
} from '../../settings/connections';
import {
  CHAT_BACKFILL_STEP,
  chatBackfillLine,
  QrBox,
  qrModeOf,
  useCountdown,
} from '../../settings/WhatsAppSettingsPage';
import { WizardButton } from '../ui';
import { CardEyebrow, ProgressRows } from './parts';
import styles from './WhatsAppPairing.module.css';

/**
 * The WhatsApp pairing of Nutzer 08 / M6 and the checklist dialog Nutzer 14: the status of the user's session (polled
 * by `useWhatsappStatus`), the 60-second code with its countdown, start (once, on opening) and refresh. D-04: the
 * code is scanned in WhatsApp's „Verknüpfte Geräte", there is no BRIMO phone app.
 */

export const SCAN_STEPS = 'WhatsApp öffnen · Verknüpfte Geräte · Gerät hinzufügen · Code scannen';

export interface WhatsappPairing {
  status: WhatsappStatus | undefined;
  state: WhatsappState;
  msLeft: number | null;
  busy: boolean;
  error: string | null;
  start: () => void;
  refresh: () => void;
}

export function useWhatsappPairing(autoStart: boolean): WhatsappPairing {
  const status = useWhatsappStatus();
  const connections = useConnections();
  const startMutation = useStartWhatsapp();
  const refreshMutation = useRefreshWhatsapp();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const msLeft = useCountdown(status.data?.state === 'qr' ? status.data.expiresAt : null);
  const serverState = status.data?.state ?? 'none';
  const state: WhatsappState =
    serverState === 'qr' && msLeft !== null && msLeft <= 0 ? 'expired' : serverState;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    action().catch((failure: unknown) => setError(errorMessage(failure)));
  };
  const start = () => run(() => startMutation.mutateAsync());
  const refresh = () => run(() => refreshMutation.mutateAsync());

  useEffect(() => {
    if (!autoStart || started.current || !status.data || !connections.data) return;
    if (status.data.state !== 'none' && status.data.state !== 'disconnected') return;
    started.current = true;
    if (connections.data.whatsappConfigured) start();
    else
      setError(
        'WhatsApp ist in dieser Umgebung nicht eingerichtet. Sie können den Schritt später nachholen.',
      );
    // Once per opening, after status and environment flags arrived.
  }, [autoStart, status.data, connections.data]);

  return {
    status: status.data,
    state,
    msLeft,
    busy: startMutation.isPending || refreshMutation.isPending,
    error: error ?? (status.isError ? errorMessage(status.error) : null),
    start,
    refresh,
  };
}

/** The panel of the current state: ready (code, countdown), expired (overlay), scan recognised, connected. */
export function PairingPanel({
  pairing,
  size,
  variant,
}: {
  pairing: WhatsappPairing;
  size: number;
  variant: 'wizard' | 'dialog' | 'mobile';
}) {
  const { status, state, msLeft, busy, error } = pairing;
  if (!status) return <div className={styles.placeholder} style={{ height: size + 120 }} aria-busy="true" />;

  if (state === 'pairing') {
    return (
      <div className={styles.panel} role="status">
        {variant === 'wizard' ? <CardEyebrow>Scan erkannt</CardEyebrow> : null}
        <span className={styles.pairingTitle}>
          <span className={styles.spinner} aria-hidden="true" />
          Session wird aufgebaut
        </span>
        <ProgressRows
          variant="boxed"
          rows={[
            {
              label: `Gerät erkannt${status.deviceName ? ` · ${status.deviceName}` : status.phoneE164 ? ` · ${formatPhone(status.phoneE164)}` : ''}`,
              state: 'done',
            },
            { label: status.progressText ?? 'Schlüssel werden ausgetauscht', state: 'active' },
            { label: CHAT_BACKFILL_STEP, state: 'open' },
          ]}
        />
        {error ? <p className={styles.error}>{error}</p> : null}
      </div>
    );
  }

  if (state === 'connected') {
    const chats = chatBackfillLine(status);
    return (
      <div className={styles.panel} role="status">
        <span className={styles.connected}>
          <span className={styles.connectedMark} aria-hidden="true">
            ✓
          </span>
          <span>
            <span className={styles.connectedTitle}>Verbunden</span>
            <span className={styles.connectedSub}>
              {[status.phoneE164 ? formatPhone(status.phoneE164) : null, status.deviceName]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {chats ? <span className={styles.connectedSub}>{chats}</span> : null}
          </span>
        </span>
      </div>
    );
  }

  const mode = qrModeOf(state);
  return (
    <div className={styles.panel}>
      {variant === 'wizard' ? (
        <CardEyebrow>{mode === 'expired' ? 'Abgelaufen' : 'Bereit zum Scannen'}</CardEyebrow>
      ) : null}
      <div className={styles.qr}>
        <QrBox
          mode={mode}
          qrDataUrl={status.qrDataUrl}
          progressText={status.progressText}
          size={size}
          busy={busy}
          onStart={pairing.start}
          onRefresh={pairing.refresh}
        />
      </div>
      {mode === 'qr' && msLeft !== null ? (
        <span className={styles.validity}>Gültig noch {formatCountdown(msLeft)}</span>
      ) : null}
      {mode === 'expired' ? (
        <span className={styles.steps}>
          Jeder Code ist eine Minute gültig. Halten Sie das Handy bereit, bevor Sie neu laden.
        </span>
      ) : variant === 'mobile' ? null : (
        <span className={styles.steps}>{SCAN_STEPS}</span>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
      {mode === 'qr' && variant === 'wizard' ? (
        <WizardButton variant="secondary" size="small" block loading={busy} onClick={pairing.refresh}>
          Neuen Code erzeugen
        </WizardButton>
      ) : null}
    </div>
  );
}
